import React from 'react'
import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import AdminDashboardPage from './admin/pages/dashboard.page'
import SuperAdminDashboardPage from './admin/pages/super-dashboard.page'
import VehicleAdminPage from './admin/pages/vehicle-admin.page'
import CreateVehiclePage from './admin/pages/create-vehicle.page'
import CreateStandPage from './admin/pages/create-stand.page'
import CreateCompanyPage from './admin/pages/create-company.page'
import CreateUserPage from './admin/pages/create-user.page'
import { StandPage } from './admin/pages/stand.page'
import { CompanyPage } from './admin/pages/company.page'
import { UserPage } from './admin/pages/user.page'
import SideMenu from './shared/components/side-menu.component'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { AdminApi } from './admin/admin.api'
import { AdminActions } from './admin/admin.store'
import { useAlert } from 'react-alert'
import { SoldVehiclesPage } from './admin/pages/sold-vehicles.page'
import { VehiclePage } from './admin/pages/vehicle.page'
import { RoutesEnum } from './routes.constants'
import { IStoreInterface } from './configs/store.config'
import { LoggedUser } from './shared/shared.models'
import { RoleEnum } from './shared/shared.enums'
import { AnalyticsPage } from './admin/pages/analytics.page'
import { ContactsPage } from './admin/pages/contacts.page'
import CreateContactPage from './admin/pages/create-contact.page'
import { ContactPage } from './admin/pages/contact.page'
import CreateSupplierPage from './admin/pages/create-supplier.page'
import { SupplierPage } from './admin/pages/supplier.page'
import { EventsPage } from './admin/pages/events.page'
import CreateEventPage from './admin/pages/create-event.page'
import { EventPage } from './admin/pages/event.page'

const PrivateRoutes = () => {
    // Hooks Initialization
    const dispatch = useDispatch()

    const alert_error = useAlert()
    const loggedUser = useSelector(
        (state: IStoreInterface) => state.adminReducer.user as LoggedUser
    )

    let pathInitial = ''

    if (loggedUser.isSuperUser) pathInitial = '/superuser/companies'
    else if (loggedUser.isActive) pathInitial = '/admin/vehicles'
    else pathInitial = '/login'

    /**
     * Function to fetch a list of users
     */
    const fetchUsers = React.useCallback(() => {
        if (
            loggedUser.companyUuid &&
            (loggedUser.role === RoleEnum.ADMIN ||
                loggedUser.role === RoleEnum.MANAGER ||
                loggedUser.isSuperUser)
        ) {
            AdminApi.methods
                .getUsers(loggedUser.companyUuid)
                .then((res) => {
                    dispatch(
                        AdminActions.methods.getUsersSuccessAction(
                            res.data.users,
                            loggedUser.companyUuid
                        )
                    )
                })
                .catch((e) => {
                    alert_error.error(
                        'Houve um erro a ir buscar a lista de utilizadores, por favor tente mais tarde'
                    )
                })
        }
    }, [alert_error, dispatch, loggedUser])

    /**
     * Function to fetch a list of stands
     */
    const fetchStands = React.useCallback(() => {
        if (loggedUser.companyUuid) {
            AdminApi.methods
                .getStands(loggedUser.companyUuid)
                .then((res) => {
                    dispatch(
                        AdminActions.methods.getStandsSuccessAction(
                            res.data.stands
                        )
                    )
                })
                .catch((e) => {
                    alert_error.error(
                        'Houve um erro a ir buscar a lista de stands, por favor tente mais tarde'
                    )
                })
        }
    }, [alert_error, dispatch, loggedUser])

    /**
     * Function to fetch the list of extras
     */
    // categories are lazy-loaded where needed to avoid requesting them on every admin route mount

    /**
     * Fetchs the list of cost types
     */
    const fetchCostTypes = React.useCallback(() => {
        AdminApi.methods
            .getCostTypes()
            .then((res) => {
                dispatch(
                    AdminActions.methods.getCostTypesSuccessAction(
                        res.data.costTypes
                    )
                )
            })
            .catch((e) => {
                alert_error.error(
                    'Houve um erro a ir buscar os tipos de custo, por favor tente mais tarde'
                )
            })
    }, [alert_error, dispatch])

    const StandPageWrapper = () => {
        const { standId } = useParams<{ standId: string }>()
        return <StandPage standId={standId || ''} />
    }

    const CompanyPageWrapper = () => {
        const { companyId } = useParams<{ companyId: string }>()
        return <CompanyPage companyId={companyId || ''} />
    }

    const UserPageWrapper = () => {
        const { userId } = useParams<{ userId: string }>()
        return <UserPage userId={userId || ''} />
    }

    const VehiclePageWrapper = () => {
        const { vehicleId } = useParams<{ vehicleId: string }>()
        return <VehiclePage vehicleId={vehicleId || ''} />
    }

    const ContactPageWrapper = () => {
        const { contactId } = useParams<{ contactId: string }>()
        return <ContactPage contactId={contactId || ''} />
    }

    const EventPageWrapper = () => {
        const { eventId } = useParams<{ eventId: string }>()
        return <EventPage eventId={eventId || ''} />
    }

    const CreateStandPageWrapper = () => {
        const { companyId } = useParams<{ companyId: string }>()
        return <CreateStandPage companyId={companyId || ''} />
    }

    const CreateUserPageWrapper = () => {
        const { companyId } = useParams<{ companyId: string }>()
        return <CreateUserPage companyId={companyId || ''} />
    }

    const CreateSupplierPageWrapper = () => {
        const { companyId } = useParams<{ companyId: string }>()
        return <CreateSupplierPage companyId={companyId || ''} />
    }

    const SupplierPageWrapper = () => {
        const { supplierId } = useParams<{ supplierId: string }>()
        return <SupplierPage supplierId={supplierId || ''} />
    }

    // Effects
    React.useEffect(() => {
        fetchUsers()
        fetchStands()
        fetchCostTypes()
    }, [fetchUsers, fetchStands, fetchCostTypes])

    return (
        <>
            <SideMenu />
            <MainContainer>
                <Routes>
                    {/* Redirect root to appropriate dashboard */}
                    <Route
                        path="/"
                        element={<Navigate to={pathInitial} replace />}
                    />

                    {/* Admin routes */}
                    <Route
                        path="/admin/vehicles"
                        element={<AdminDashboardPage />}
                    />
                    <Route
                        path="/admin/admin-vehicles"
                        element={<VehicleAdminPage />}
                    />
                    <Route
                        path="/admin/vehicle-details/:vehicleId/*"
                        element={<VehiclePageWrapper />}
                    />
                    <Route
                        path="/admin/vehicles/create"
                        element={<CreateVehiclePage />}
                    />
                    <Route
                        path="/admin/sold-vehicles"
                        element={<SoldVehiclesPage />}
                    />
                    <Route
                        path="/admin/analytics/"
                        element={<AnalyticsPage />}
                    />
                    <Route
                        path={RoutesEnum.CREATE_VEHICLE}
                        element={<CreateVehiclePage />}
                    />

                    {/* Contact routes */}
                    <Route
                        path="/account/contacts/"
                        element={<ContactsPage />}
                    />
                    <Route
                        path="/admin/contacts/:contactId/*"
                        element={<ContactPageWrapper />}
                    />
                    <Route
                        path={RoutesEnum.CREATE_CONTACT}
                        element={<CreateContactPage />}
                    />

                    {/* Event routes */}
                    <Route path="/admin/events" element={<EventsPage />} />
                    <Route
                        path={RoutesEnum.CREATE_EVENT}
                        element={<CreateEventPage />}
                    />
                    <Route
                        path="/admin/events/:eventId/*"
                        element={<EventPageWrapper />}
                    />

                    {/* Superuser routes */}
                    <Route
                        path="/superuser/companies"
                        element={<SuperAdminDashboardPage />}
                    />
                    <Route
                        path="/superuser/stand-details/:standId/*"
                        element={<StandPageWrapper />}
                    />
                    <Route
                        path="/superuser/supplier-details/:supplierId/*"
                        element={<SupplierPageWrapper />}
                    />
                    <Route
                        path="/superuser/company-details/:companyId/*"
                        element={<CompanyPageWrapper />}
                    />
                    <Route
                        path={RoutesEnum.CREATE_COMPANY}
                        element={<CreateCompanyPage />}
                    />
                    <Route
                        path={RoutesEnum.CREATE_STAND}
                        element={<CreateStandPageWrapper />}
                    />
                    <Route
                        path={RoutesEnum.CREATE_SUPPLIER}
                        element={<CreateSupplierPageWrapper />}
                    />

                    {/* User routes */}
                    <Route
                        path="/account/user/:userId/*"
                        element={<UserPageWrapper />}
                    />
                    <Route
                        path={RoutesEnum.CREATE_USER}
                        element={<CreateUserPageWrapper />}
                    />

                    {/* 404 */}
                    <Route path="*" element={<div>404 not found</div>} />
                </Routes>
            </MainContainer>
        </>
    )
}

export default PrivateRoutes

const MainContainer = styled('div')`
    margin-left: 60px;
    padding: 32px;
    min-height: 500px;
`
